import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box } from '@mui/material';

const showAds = process.env.REACT_APP_SHOW_ADS === 'true';

const DynamicAdSense = ({
  adType = 'auto',
  fullWidth = true,
  onError = null,
  onLoad = null,
}) => {
  const [adConfig, setAdConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const adRef = useRef(null);
  const [adLoaded, setAdLoaded] = useState(false);

  useEffect(() => {
    fetchAdConfig();
  }, [adType]);

  useEffect(() => {
    if (adConfig && !adLoaded && !adConfig.testMode) {
      loadGoogleAdsScript();
    }
  }, [adConfig, adLoaded]);

  const fetchAdConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `/.netlify/functions/get-ad-config?type=${adType}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch ad config: ${response.status}`);
      }

      const config = await response.json();

      if (!config.clientId || !config.slotId) {
        throw new Error('Invalid ad configuration received');
      }

      setAdConfig(config);
    } catch (err) {
      console.error('Error fetching ad config:', err);
      setError(err.message);
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleAdsScript = () => {
    if (document.querySelector('script[src*="adsbygoogle.js"]')) {
      initializeAd();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adConfig.clientId}`;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      initializeAd();
    };

    script.onerror = () => {
      setError('Failed to load Google AdSense script');
    };

    document.head.appendChild(script);
  };

  const initializeAd = () => {
    try {
      if ((window as any).adsbygoogle && adRef.current) {
        ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push(
          {}
        );
        setAdLoaded(true);
        if (onLoad) onLoad();
      }
    } catch (err) {
      console.error('Error initializing ad:', err);
      setError('Failed to initialize advertisement');
    }
  };

  const retryLoadAd = () => {
    setAdLoaded(false);
    fetchAdConfig();
  };

  return (
    <Box
      className='meme'
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
        maxWidth: '100dvw',
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: { xs: 1.5, md: 0 },
      }}
    >
      {loading ? (
        <Typography variant='h6' color='text.primary'>
          Loading Ad here, kindly wait
        </Typography>
      ) : error ? (
        <Typography variant='h6' color='text.primary'>
          Something went wrong while fetching Ad, please scroll to see next post. 
        </Typography>
      ) : !adConfig ? (
        <Typography variant='h6' color='text.primary'>
          Something went wrong while fetching Ad, please scroll to see next post. 
        </Typography>
      ) : !showAds ? (
        <Typography variant='h6' color='text.primary'>
          Ads are turned off
        </Typography>
      ) : adConfig.testMode ? (
        <Box
          sx={{
            width: '100%',
            minHeight: '240px',
            border: '2px dashed',
            borderColor: 'grey.400',
            display: 'flex',
            position: 'relative',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'grey.50',
            borderRadius: 1,
          }}
        >
          <Typography
            variant='h6'
            color='text.secondary'
            sx={{
              textAlign: 'center',
              fontWeight: 'medium',
            }}
          >
            Ads Goes Here
          </Typography>
        </Box>
      ) : (
        <div
          style={{
            width: '100%',
            display: 'block',
            minHeight: '240px',
            position: 'relative',
          }}
        >
          <ins
            ref={adRef}
            className='adsbygoogle'
            style={{
              display: 'block',
              width: '100%',
              minHeight: '240px',
            }}
            data-ad-client={adConfig.clientId}
            data-ad-slot={adConfig.slotId}
            // data-ad-format={adConfig.format || adType}
            // data-full-width-responsive={fullWidth.toString()}
            data-ad-test={adConfig.testMode ? 'on' : undefined}
          />
        </div>
      )}
    </Box>
  );
};

export default DynamicAdSense;
